// Shared Comment Markdown syntax v1, adapted from the iOS handoff parser.
const markdownit = require('markdown-it')
const katex = require('katex')
var md = markdownit({ html: false, linkify: true, breaks: true, typographer: false })
var escape = md.utils.escapeHtml

function isEscaped(source, index) {
  var count = 0
  while (index > 0 && source[--index] === '\\') count++
  return count % 2 === 1
}

function closingIndex(source, delimiter, start) {
  var end = source.indexOf(delimiter, start)
  while (end >= 0 && isEscaped(source, end)) end = source.indexOf(delimiter, end + delimiter.length)
  return end
}

// A comment explicitly authors math with \(...\), \[...\], $$...$$ or $...$.
// Bare dollar amounts, numeric-only dollar pairs, and escaped dollars remain prose.
// No second auto-render pass runs over prose, links, images or code tokens.
function inlineMath(state, silent) {
  var pos = state.pos,
    source = state.src,
    opening,
    closing,
    display = false
  if (source.slice(pos, pos + 2) === '\\(') {
    opening = '\\('
    closing = '\\)'
  } else if (source.slice(pos, pos + 2) === '\\[') {
    opening = '\\['
    closing = '\\]'
    display = true
  } else if (source.slice(pos, pos + 2) === '$$') {
    opening = closing = '$$'
    display = true
  } else if (source[pos] === '$' && source[pos - 1] !== '$') {
    opening = closing = '$'
  } else return false
  var begin = pos + opening.length,
    end = closingIndex(source, closing, begin)
  if (end < 0 || end + closing.length > state.posMax || end === begin) {
    // Do not let CommonMark consume the slash of an unfinished authoring delimiter.
    if (opening[0] !== '\\') return false
    if (!silent) state.push('text', '', 0).content = opening
    state.pos = begin
    return true
  }
  var latex = source.slice(begin, end)
  // Multiline display math belongs to the block parser, which knows the
  // current list/quote boundaries. An inline fallback must not cross them.
  if (display && /\n/.test(latex)) {
    if (opening[0] !== '\\') return false
    if (!silent) state.push('text', '', 0).content = opening
    state.pos = begin
    return true
  }
  if (opening === '$') {
    if (/\s/.test(source[begin]) || /\s/.test(source[end - 1]) || /\d/.test(source[end + 1] || ''))
      return false
    if (/\n/.test(latex) || /^[+-]?[\d.,\s]+$/.test(latex)) return false
  }
  if (!silent) {
    var token = state.push(display ? 'comment_math_display_inline' : 'comment_math_inline', '', 0)
    token.content = latex
    token.meta = { raw: source.slice(pos, end + closing.length), display: display }
  }
  state.pos = end + closing.length
  return true
}

function blockMath(state, startLine, endLine, silent) {
  if (state.sCount[startLine] - state.blkIndent >= 4) return false
  var first = state.bMarks[startLine] + state.tShift[startLine]
  var opening = state.src.slice(first, first + 2)
  if (opening !== '$$' && opening !== '\\[') return false
  var closing = opening === '$$' ? '$$' : '\\]'
  var finalLine = startLine,
    lines = [],
    found = false
  for (; finalLine < endLine; finalLine++) {
    // bMarks/tShift have already been adjusted by the enclosing quote/list.
    // Reading a global source slice would reintroduce '>' markers and could
    // accidentally find a closing delimiter in a later, unrelated container.
    if (
      finalLine > startLine &&
      (state.tShift[finalLine] < 0 ||
        (state.sCount[finalLine] < state.blkIndent && !state.isEmpty(finalLine)))
    )
      break
    var line = state.src.slice(state.bMarks[finalLine] + state.tShift[finalLine], state.eMarks[finalLine])
    if (finalLine === startLine) line = line.slice(opening.length)
    var end = closingIndex(line, closing, 0)
    if (end >= 0) {
      if (line.slice(end + closing.length).trim()) return false
      lines.push(line.slice(0, end))
      found = true
      break
    }
    lines.push(line)
  }
  if (!found) return false
  if (silent) return true
  var token = state.push('comment_math_block', 'div', 0)
  token.block = true
  token.content = lines.join('\n').trim()
  token.map = [startLine, finalLine + 1]
  token.meta = { raw: opening + lines.join('\n') + closing, display: true }
  state.line = finalLine + 1
  return true
}

md.inline.ruler.before('escape', 'comment_math', inlineMath)
md.block.ruler.before('paragraph', 'comment_math', blockMath, {
  alt: ['paragraph', 'reference', 'blockquote', 'list']
})

function renderMath(tokens, index, options, env) {
  var token = tokens[index],
    display = token.meta.display
  env.mathCount = (env.mathCount || 0) + 1
  var html
  try {
    // A new macro object per expression prevents definitions leaking between comments.
    html = katex.renderToString(token.content, {
      displayMode: display,
      throwOnError: true,
      trust: false,
      strict: 'ignore',
      maxSize: 20,
      maxExpand: 1000,
      output: 'htmlAndMathml',
      macros: {}
    })
  } catch (_) {
    env.mathErrors = (env.mathErrors || 0) + 1
    html = '<span class="comment-math-error">' + escape(token.meta.raw) + '</span>'
  }
  var tag = token.block ? 'div' : 'span'
  return (
    '<' +
    tag +
    ' class="comment-math-' +
    (display ? 'display' : 'inline') +
    '">' +
    html +
    '</' +
    tag +
    '>' +
    (token.block ? '\n' : '')
  )
}
md.renderer.rules.comment_math_inline = renderMath
md.renderer.rules.comment_math_display_inline = renderMath
md.renderer.rules.comment_math_block = renderMath

// Only network links are interactive. Never forward file:, javascript:, or app schemes.
function safeURL(value) {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return null
  try {
    var url = new URL(value)
    return /^(https?:)$/.test(url.protocol) ? url.href : null
  } catch (_) {
    return null
  }
}
var originalValidate = md.validateLink
md.validateLink = function (url) {
  return originalValidate(url) && /^https?:\/\//i.test(url)
}
var defaultLinkOpen =
  md.renderer.rules.link_open ||
  function (tokens, index, options, env, self) {
    return self.renderToken(tokens, index, options)
  }
md.renderer.rules.link_open = function (tokens, index, options, env, self) {
  tokens[index].attrSet('rel', 'noreferrer noopener nofollow')
  return defaultLinkOpen(tokens, index, options, env, self)
}
var defaultImage = md.renderer.rules.image
md.renderer.rules.image = function (tokens, index, options, env, self) {
  tokens[index].attrSet('referrerpolicy', 'no-referrer')
  return defaultImage(tokens, index, options, env, self)
}
md.renderer.rules.table_open = function () {
  return '<div class="comment-table-scroll"><table>\n'
}
md.renderer.rules.table_close = function () {
  return '</table></div>\n'
}

function renderMarkdown(source) {
  var env = { mathCount: 0, mathErrors: 0 }
  var html = md.render(typeof source === 'string' ? source : '', env)
  return { html: html, mathCount: env.mathCount, mathErrors: env.mathErrors }
}

module.exports = { renderMarkdown, safeURL, syntaxVersion: 1 }
